import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger } from '@/components/ui/dialog';
import { ExternalLink, Pencil, Trash2, ArrowRight, FolderOpen, X } from 'lucide-react';
import { getVideoEmbedInfo } from '@/lib/utils/video';
import type { Tables } from '@/lib/supabase/supabase-types';
import { cn } from '@/lib/utils';

type PortfolioProject = Tables<'portfolio_projects'>;

interface PortfolioProjectCardProps {
  project: PortfolioProject;
  isOwner?: boolean;
  onEdit?: (project: PortfolioProject) => void;
  onDelete?: (project: PortfolioProject) => void;
  className?: string;
}

export function PortfolioProjectCard({
  project,
  isOwner = false,
  onEdit,
  onDelete,
  className
}: PortfolioProjectCardProps) {
  const [isImageError, setIsImageError] = useState(false);

  // Truncate description for card display
  const truncatedDescription = project.description.length > 200
    ? project.description.substring(0, 200) + '...'
    : project.description;

  // Check if project URL is a video
  const videoInfo = getVideoEmbedInfo(project.project_url || '');

  return (
    <div className={cn(
      "rounded-[15px] border-2 border-slate-200 bg-white overflow-hidden flex flex-col h-full",
      className
    )}>
      {/* Image/Video Section */}
      <div className="relative">
        {videoInfo.embedUrl ? (
          <div className="aspect-video w-full overflow-hidden">
            <iframe
              src={videoInfo.embedUrl}
              title={project.title}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : project.image_url && !isImageError ? (
          <div className="aspect-video w-full overflow-hidden">
            <img
              src={project.image_url}
              alt={project.title}
              className="h-full w-full object-cover transition-transform hover:scale-105"
              onError={() => setIsImageError(true)}
            />
          </div>
        ) : (
          <div
            className="aspect-video w-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(103.67deg, rgba(209, 224, 252, 1) 0%, rgba(241, 245, 249, 1) 100%)'
            }}
          >
            <FolderOpen className="h-6 w-6 text-slate-400" />
          </div>
        )}

        {/* Owner Actions - positioned at top right */}
        {isOwner && (
          <div className="absolute top-3 right-3 flex gap-1">
            {onEdit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onEdit(project)}
                className="h-8 w-8 p-0 bg-white/90 hover:bg-white shadow-sm text-slate-400"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onDelete(project)}
                className="h-8 w-8 p-0 bg-white/90 hover:bg-white text-red-600 hover:text-red-700 shadow-sm"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6 flex flex-col flex-1">
        {/* Title */}
        <h3 className="text-lg font-bold text-slate-900 leading-tight line-clamp-2 mb-3">
          {project.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 leading-relaxed flex-1 line-clamp-4">
          {truncatedDescription}
        </p>

        {/* View Case Study Link */}
        <Dialog>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 mt-4 text-[#4669ed] font-semibold text-base hover:underline">
              View Full Case Study
              <ArrowRight className="h-5 w-5" />
            </button>
          </DialogTrigger>
          <DialogContent className="!max-w-4xl w-[95vw] max-h-[90vh] p-0 rounded-[40px] overflow-hidden flex flex-col" showCloseButton={false}>
            {/* Close Button - Top Right */}
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-50 h-10 w-10 rounded-full bg-white/90 hover:bg-white shadow-sm"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogClose>

            {/* Header Section with Title and Subtitle */}
            <div className="px-6 sm:px-10 lg:px-12 pt-8 sm:pt-10 pb-6 flex-shrink-0">
              <DialogHeader className="mb-0">
                <DialogTitle className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight mb-2">
                  {project.title}
                </DialogTitle>
              </DialogHeader>
            </div>

            {/* Image/Video Section with Padding */}
            <div className="px-6 sm:px-10 lg:px-12 pb-6 flex-shrink-0">
              {videoInfo.embedUrl ? (
                <div className="w-full overflow-hidden rounded-lg aspect-video bg-slate-100">
                  <iframe
                    src={videoInfo.embedUrl}
                    title={project.title}
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : project.image_url && !isImageError ? (
                <div className="w-full overflow-hidden rounded-lg">
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-auto object-contain max-h-[500px] mx-auto"
                    onError={() => setIsImageError(true)}
                  />
                </div>
              ) : (
                <div
                  className="w-full h-48 sm:h-64 flex items-center justify-center rounded-lg"
                  style={{
                    background: 'linear-gradient(103.67deg, rgba(209, 224, 252, 1) 0%, rgba(241, 245, 249, 1) 100%)'
                  }}
                >
                  <FolderOpen className="h-16 w-16 text-slate-300" />
                </div>
              )}
            </div>

            {/* Content Section - Description (Scrollable) */}
            <div className="px-6 sm:px-10 lg:px-12 pb-6 overflow-y-auto flex-1 max-h-[300px]">
              <article className="prose prose-slate max-w-none">
                <div className="text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {project.description}
                </div>
              </article>
            </div>

            {/* Footer with Close Button (left) and View Live Project Button (right) */}
            <DialogFooter className="px-6 sm:px-10 lg:px-12 py-6 border-t border-slate-200 flex flex-row sm:flex-row justify-between items-center sm:justify-between flex-shrink-0">
              <DialogClose asChild>
                <Button variant="outline" className="rounded-[48px] px-8 py-3 h-auto">
                  <X className="h-4 w-4 mr-2" />
                  Close
                </Button>
              </DialogClose>
              {project.project_url && (
                <Button asChild className="rounded-[48px] bg-[#4669ed] hover:bg-[#3654d4] px-8 py-3 h-auto">
                  <a
                    href={project.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-base font-medium"
                  >
                    <ExternalLink className="h-5 w-5" />
                    View Live Project
                  </a>
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
